
import { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  MoreVertical,
  Download,
  TrendingUp,
  TrendingDown,
  Plus,
  Search,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Calendar,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Settings,
  AlertCircle,
} from "lucide-react";
import { CreateInvoiceDialog } from "@/components/sales/CreateInvoiceDialog";
import { invoiceService, Invoice, InvoiceFilters } from '@/services/invoice.service';
import { clientService } from '@/services/client.service';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getCurrencySymbol } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

export default function Invoices() {
  const { hasSubModulePermission } = useAuthStore();
  const computeTotals = (inv: Invoice | null) => {
    if (!inv) return { subtotal: 0, totalTax: 0, total: 0 };
    // Prefer backend totals if available
    const hasBackendTotals = typeof (inv as any).subtotal === 'number' && typeof (inv as any).totalAmount === 'number';
    if (hasBackendTotals) {
      const subtotal = (inv as any).subtotal as number;
      const totalAmount = (inv as any).totalAmount as number;
      const totalTax = Math.max(0, totalAmount - subtotal);
      return { subtotal, totalTax, total: totalAmount };
    }
    const subtotal = (inv.items || []).reduce((sum, it) => {
      const qty = Number(it.quantity || 0);
      const rate = Number(it.rate || 0);
      return sum + qty * rate;
    }, 0);
    const total = (inv.items || []).reduce((sum, it) => sum + Number(it.amount || 0), 0);
    const totalTax = Math.max(0, total - subtotal);
    return { subtotal, totalTax, total };
  };

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [quantityDisplayEdit, setQuantityDisplayEdit] = useState<'qty' | 'hours' | 'qty_hours'>('qty');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');

  const invoiceDateRef = useRef<HTMLInputElement>(null);
  const dueDateRef = useRef<HTMLInputElement>(null);

  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    paidAmount: 0,
    overdueAmount: 0,
    pendingAmount: 0,
  });

  useEffect(() => {
    loadInvoices();
    loadClients();
    loadAllInvoicesForStats(); // Recalculate stats when filters change
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    loadAllInvoicesForStats(); // Recalculate stats when currency changes
  }, [selectedCurrency]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const response = await clientService.getClients({ limit: 1000 });
      if (response && Array.isArray(response.data)) {
        setClients(response.data);
      } else if (response && Array.isArray(response)) {
        setClients(response);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  // Helper function to calculate item amount with tax
  const calculateItemAmount = (quantity: number, rate: number, tax: string) => {
    const baseAmount = quantity * rate;
    let taxAmount = 0;

    if (tax && tax !== 'No Tax') {
      const taxPercentage = parseFloat(tax.replace('%', ''));
      if (!isNaN(taxPercentage)) {
        taxAmount = (baseAmount * taxPercentage) / 100;
      }
    }

    return baseAmount + taxAmount;
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading invoices with filters:', filters);
      const response = await invoiceService.getInvoices({ ...filters, page: currentPage, limit: pageSize });
      console.log('📊 Invoices response:', response);

      setInvoices(response.data);
      const total = response.total ?? response.data.length;
      const totalPagesCalc = Math.max(1, Math.ceil(total / pageSize));
      setTotalInvoices(total);
      setTotalPages(totalPagesCalc);
      // Don't calculate stats here - let loadAllInvoicesForStats handle it

      // Suppress success/info toasts on load per request
    } catch (error: any) {
      console.error('❌ Error loading invoices:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load invoices';
      toast.error(errorMessage);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { label: "All statuses", value: "all" },
    { label: "Paid", value: "paid" },
    { label: "Pending", value: "pending" },
    { label: "Overdue", value: "overdue" },
  ];

  const loadAllInvoicesForStats = async () => {
    try {
      // Load all invoices without pagination for accurate totals, filtered by selected currency
      const response = await invoiceService.getInvoices({ ...filters, limit: 1000 });
      calculateStats(response.data);
    } catch (error) {
      console.error('Error loading all invoices for stats:', error);
    }
  };

  const calculateStats = (invoiceData: Invoice[]) => {
    const totals = {
      totalInvoices: 0,
      totalAmount: 0,
      paidAmount: 0,
      overdueAmount: 0,
      pendingAmount: 0,
    };

    const getSubtotal = (invoice: Invoice) => {
      if (typeof invoice.subtotal === 'number') {
        return invoice.subtotal;
      }
      const amount = typeof invoice.totalAmount === 'number' ? invoice.totalAmount : Number(invoice.totalAmount ?? 0);
      return Number.isFinite(amount) ? amount : 0;
    };

    // Filter invoices by selected currency
    const filteredInvoices = invoiceData.filter(invoice =>
      invoice.currency?.toUpperCase() === selectedCurrency.toUpperCase()
    );

    totals.totalInvoices = filteredInvoices.length;

    filteredInvoices.forEach(invoice => {
      const subtotal = getSubtotal(invoice);
      totals.totalAmount += subtotal;

      switch (invoice.status) {
        case 'paid':
          totals.paidAmount += subtotal;
          break;
        case 'overdue':
          totals.overdueAmount += subtotal;
          break;
        case 'pending':
          totals.pendingAmount += subtotal;
          break;
      }
    });

    setStats(totals);
  };

  const handleFilterChange = (key: keyof InvoiceFilters, value: any) => {
    setFilters(prev => {
      const next = { ...prev };
      if (value === undefined || value === null || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const openEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setEditOpen(true);
  };

  useEffect(() => {
    if (!selectedInvoice) return;
    try {
      const stored = localStorage.getItem(`invoiceQuantityDisplay:${selectedInvoice.id}`);
      if (stored === 'qty' || stored === 'hours' || stored === 'qty_hours') {
        setQuantityDisplayEdit(stored);
        return;
      }
    } catch { }
    const items = selectedInvoice.items || [];
    const allHours = items.length > 0 && items.every(i => (i.unit || '').toLowerCase().includes('hour'));
    const anyHours = items.some(i => (i.unit || '').toLowerCase().includes('hour'));
    setQuantityDisplayEdit(allHours ? 'hours' : (anyHours ? 'qty_hours' : 'qty'));
  }, [selectedInvoice]);

  const openDeleteInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedInvoice) return;

    try {
      setSubmitting(true);
      await invoiceService.deleteInvoice(selectedInvoice.id);

      setDeleteOpen(false);
      setSelectedInvoice(null);

      await loadInvoices();

      toast.success(`${selectedInvoice.invoiceNumber} has been deleted successfully.`);
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvoiceCreated = (invoice: Invoice) => {
    console.log('✅ Invoice created, refreshing list:', invoice);
    loadInvoices(); // Refresh to get updated stats and the new invoice
  };

  // Status color helper removed

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    const locale = currency?.toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleExportPDF = async () => {
    try {
      // Get all invoices for export with current filters
      const resp = await invoiceService.getInvoices({ ...filters, page: 1, limit: 1000 });
      const rows = resp.data || [];

      const dateStr = new Date().toISOString().slice(0, 10);
      const title = `Invoices (${rows.length}) - ${dateStr}`;

      // Create a temporary div with the invoice table
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '800px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';

      tempDiv.innerHTML = `
        <h1 style="font-size: 24px; margin: 0 0 20px; color: #111827;">${title}</h1>
        <div style="color: #6b7280; font-size: 12px; margin-bottom: 20px;">
          Filters: ${Object.entries(filters).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}: ${v}`).join(', ') || 'None'}
        </div>
        <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr style="background-color: #f9fafb;">
              <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">Invoice #</th>
              <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">Customer</th>
              <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">Date</th>
              <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">Due Date</th>
              <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(inv => {
        const totals = computeTotals(inv);
        return `
              <tr>
                <td style="border: 1px solid #e5e7eb; padding: 8px;">${inv.invoiceNumber || ''}</td>
                <td style="border: 1px solid #e5e7eb; padding: 8px;">${inv.customer?.name || ''}</td>
                <td style="border: 1px solid #e5e7eb; padding: 8px;">${formatDate(inv.invoiceDate)}</td>
                <td style="border: 1px solid #e5e7eb; padding: 8px;">${formatDate(inv.dueDate)}</td>
                <td style="border: 1px solid #e5e7eb; padding: 8px;">${formatCurrency(totals.subtotal, inv.currency)}</td>
              </tr>
              `;
      }).join('')}
          </tbody>
          <tfoot>
            <tr style="background-color: #f9fafb; font-weight: bold;">
              <td style="border: 1px solid #e5e7eb; padding: 8px;" colspan="4">Total</td>
              <td style="border: 1px solid #e5e7eb; padding: 8px;">${formatCurrency(rows.reduce((sum, inv) => sum + computeTotals(inv).subtotal, 0))}</td>
            </tr>
          </tfoot>
        </table>
      `;

      document.body.appendChild(tempDiv);

      // Convert to canvas and then to PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      // Remove temporary div
      document.body.removeChild(tempDiv);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save(`invoices-${dateStr}.pdf`);
      toast.success('PDF downloaded successfully!');

    } catch (e) {
      console.error('Export failed', e);
      toast.error('Failed to export invoices');
    }
  };

  const handleDownloadInvoicePDF = async (invoice: Invoice) => {
    try {
      const totals = computeTotals(invoice);
      const currencySymbol = getCurrencySymbol(invoice.currency);

      // Create a temporary div with the invoice details
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '800px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.padding = '40px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.fontSize = '14px';

      tempDiv.innerHTML = `
        <div style="margin-bottom: 40px;">
          <h1 style="font-size: 32px; margin: 0 0 10px; color: #1f2937; font-weight: bold;">INVOICE</h1>
          <div style="font-size: 18px; color: #6b7280; margin-bottom: 20px;">${invoice.invoiceNumber}</div>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 40px;">
          <div style="flex: 1; margin-right: 40px;">
            <h3 style="font-size: 16px; margin: 0 0 10px; color: #374151; font-weight: bold;">Bill To:</h3>
            <div style="color: #6b7280; line-height: 1.6;">
              ${invoice.customer?.name || 'Unknown Customer'}<br>
              ${invoice.billToAddress?.address1 || (invoice.billToAddress as any)?.address || ''}<br>
              ${invoice.billToAddress?.address2 ? `${invoice.billToAddress.address2}<br>` : ''}
              ${invoice.billToAddress?.city ? `${invoice.billToAddress.city}, ` : ''}
              ${invoice.billToAddress?.state || ''} ${invoice.billToAddress?.postalCode || ''}<br>
              ${invoice.billToAddress?.country || ''}
            </div>
          </div>
          <div style="flex: 1;">
            <h3 style="font-size: 16px; margin: 0 0 10px; color: #374151; font-weight: bold;">Invoice Details:</h3>
            <div style="color: #6b7280; line-height: 1.6;">
              <div style="margin-bottom: 8px;"><strong>Invoice Date:</strong> ${formatDate(invoice.invoiceDate)}</div>
              <div style="margin-bottom: 8px;"><strong>Due Date:</strong> ${formatDate(invoice.dueDate)}</div>
              <div style="margin-bottom: 8px;"><strong>Currency:</strong> ${invoice.currency}</div>
              <div style="margin-bottom: 8px;"><strong>Status:</strong> ${invoice.status.toUpperCase()}</div>
            </div>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background-color: #f9fafb;">
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: left; font-weight: bold; color: #374151;">Item</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: left; font-weight: bold; color: #374151;">Description</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: center; font-weight: bold; color: #374151;">${(() => { const allHours = (invoice.items || []).length > 0 && (invoice.items || []).every(i => (i.unit || '').toLowerCase().includes('hour')); const anyHours = (invoice.items || []).some(i => (i.unit || '').toLowerCase().includes('hour')); return allHours ? 'Hours' : (anyHours ? 'Qty/Hours' : 'Qty'); })()}</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: right; font-weight: bold; color: #374151;">Rate</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: center; font-weight: bold; color: #374151;">Tax</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: right; font-weight: bold; color: #374151;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items?.map(item => `
              <tr>
                <td style="border: 1px solid #e5e7eb; padding: 12px; color: #374151;">${item.description || '-'}</td>
                <td style="border: 1px solid #e5e7eb; padding: 12px; color: #6b7280;">${item.longDescription || '-'}</td>
                <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: center; color: #374151;">${item.quantity}</td>
                <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: right; color: #374151;">${currencySymbol}${item.rate.toFixed(2)}</td>
                <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: center; color: #374151;">${item.tax || 'No Tax'}</td>
                <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: right; color: #374151; font-weight: bold;">${currencySymbol}${item.amount.toFixed(2)}</td>
              </tr>
            `).join('') || '<tr><td colspan="6" style="border: 1px solid #e5e7eb; padding: 20px; text-align: center; color: #6b7280;">No items found</td></tr>'}
          </tbody>
        </table>

        <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
          <div style="width: 300px;">
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 2px solid #374151; border-bottom: 2px solid #374151; background-color: #f9fafb;">
              <span style="color: #374151; font-weight: bold; font-size: 16px;">Total:</span>
              <span style="color: #374151; font-weight: bold; font-size: 16px;">${currencySymbol}${totals.subtotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        ${invoice.paymentModes && invoice.paymentModes.length > 0 ? `
          <div style="margin-top: 40px; padding: 20px; background-color: #f9fafb; border-radius: 8px;">
            <h3 style="font-size: 16px; margin: 0 0 10px; color: #374151; font-weight: bold;">Payment Methods:</h3>
            <div style="color: #6b7280;">${invoice.paymentModes.join(', ')}</div>
          </div>
        ` : ''}

        <div style="margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px;">
          <p>Thank you for your business!</p>
          <p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      `;

      document.body.appendChild(tempDiv);

      // Convert to canvas and then to PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      // Remove temporary div
      document.body.removeChild(tempDiv);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `invoice-${invoice.invoiceNumber}-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);
      toast.success(`Invoice ${invoice.invoiceNumber} PDF downloaded successfully!`);

    } catch (e) {
      console.error('Invoice PDF download failed', e);
      toast.error('Failed to download invoice PDF');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground">
            Manage and track your invoices
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[120px] h-10 border-gray-300">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD $</SelectItem>
              <SelectItem value="EUR">EUR €</SelectItem>
              <SelectItem value="GBP">GBP £</SelectItem>
              <SelectItem value="INR">INR ₹</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportPDF} className="h-10">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          {hasSubModulePermission('finance', 'invoice', 'create') && (
            <Button onClick={() => setCreateDialogOpen(true)} className="h-10 rounded-lg">
              <Plus className="h-4 w-4 mr-2" />
              Create New Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalInvoices}</div>
            <p className="text-xs text-muted-foreground">All invoices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <span className="text-lg font-semibold text-muted-foreground">
              {getCurrencySymbol(selectedCurrency)}
            </span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.totalAmount, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">All invoices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Amount</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.paidAmount, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">Completed payments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue Amount</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(stats.overdueAmount, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">Past due invoices</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Management</CardTitle>
          <CardDescription>View and manage all your invoices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search invoices..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select
              value={filters.status ?? 'all'}
              onValueChange={(value) => {
                setCurrentPage(1);
                if (value === 'all') {
                  handleFilterChange('status', undefined);
                } else {
                  handleFilterChange('status', value);
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Rows per page selector moved here */}
            <Select value={String(pageSize)} onValueChange={(v) => { setCurrentPage(1); setPageSize(Number(v)); }}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 rows</SelectItem>
                <SelectItem value="10">10 rows</SelectItem>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={() => { setCurrentPage(1); loadInvoices(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Invoices Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading invoices...
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        {invoice.customer?.name || 'Unknown Customer'}
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                      <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                      <TableCell>{formatCurrency(invoice.subtotal, invoice.currency)}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                              invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                                invoice.status === 'overdue' ? 'bg-red-100 text-red-800 hover:bg-red-100' :
                                  'bg-gray-100 text-gray-800 hover:bg-gray-100'
                          }
                        >
                          {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedInvoice(invoice); setViewOpen(true); }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {hasSubModulePermission('finance', 'invoice', 'edit') && (
                              <DropdownMenuItem onClick={() => openEditInvoice(invoice)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Invoice
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDownloadInvoicePDF(invoice)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {hasSubModulePermission('finance', 'invoice', 'delete') && (
                              <DropdownMenuItem onClick={() => openDeleteInvoice(invoice)} className="text-red-600 focus:text-red-600">
                                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                                Delete Invoice
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination (match Clients style) */}
          <div className="flex items-center justify-between py-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {(invoices.length === 0 ? 0 : (currentPage - 1) * pageSize) + (invoices.length ? 1 : 0)} to {Math.min(currentPage * pageSize, totalInvoices)} of {totalInvoices} entries
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded-xl"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {(() => {
                  const pageButtons: JSX.Element[] = [];

                  if (totalPages > 3) {
                    let startPage = Math.max(1, currentPage - 1);
                    let endPage = Math.min(totalPages, currentPage + 1);

                    if (startPage === 1) {
                      endPage = Math.min(totalPages, 3);
                    }
                    if (endPage === totalPages) {
                      startPage = Math.max(1, totalPages - 2);
                    }

                    if (startPage > 1) {
                      pageButtons.push(
                        <Button
                          key={1}
                          variant={1 === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pageButtons.push(
                          <span key="ellipsis-start" className="px-2 text-muted-foreground">
                            ..
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pageButtons.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(i)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pageButtons.push(
                          <span key="ellipsis-end" className="px-2 text-muted-foreground">
                            ..
                          </span>
                        );
                      }
                      pageButtons.push(
                        <Button
                          key={totalPages}
                          variant={totalPages === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                  } else {
                    for (let i = 1; i <= totalPages; i++) {
                      pageButtons.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(i)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }
                  }

                  return pageButtons;
                })()}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="rounded-xl"
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onInvoiceCreated={handleInvoiceCreated}
      />

      {/* View Details Dialog - rich invoice preview */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-[1100px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
          <DialogHeader className="px-6 pb-0 pt-6">
            <DialogTitle className="sr-only">Invoice Details</DialogTitle>
            <DialogDescription className="sr-only">View complete invoice information and items</DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Header gradient card */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{selectedInvoice.invoiceNumber}</h1>
                      <div className="mt-2 flex items-center gap-3 text-sm text-gray-600">
                        <span>{selectedInvoice.customer?.name || 'Unknown Customer'}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total</div>
                      <div className="text-2xl font-bold">{formatCurrency(computeTotals(selectedInvoice).total, selectedInvoice?.currency)}</div>
                    </div>
                  </div>
                </div>

                {/* Details grid */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">Invoice Date</div>
                      <div className="font-medium">{formatDate(selectedInvoice.invoiceDate)}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">Due Date</div>
                      <div className="font-medium">{formatDate(selectedInvoice.dueDate)}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">Currency</div>
                      <div className="font-medium">{selectedInvoice.currency}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">Payment Modes</div>
                      <div className="font-medium">{selectedInvoice.paymentModes?.join(', ') || '-'}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">Overdue reminders</div>
                      <div className="font-medium">{selectedInvoice.preventOverdueReminders ? 'Prevented' : 'Allowed'}</div>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="text-sm text-gray-600 mb-2">Bill To</div>
                      <div className="whitespace-pre-line text-gray-900">
                        {(selectedInvoice.billToAddress?.address1 || (selectedInvoice.billToAddress as any)?.address || '-')}
                        {selectedInvoice.billToAddress?.address2 ? `\n${selectedInvoice.billToAddress.address2}` : ''}
                        {selectedInvoice.billToAddress?.city ? `\n${selectedInvoice.billToAddress.city}` : ''}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="text-sm text-gray-600 mb-2">Ship To</div>
                      <div className="whitespace-pre-line text-gray-900">
                        {(selectedInvoice.shipToAddress?.address1 || (selectedInvoice.shipToAddress as any)?.address || '-')}
                        {selectedInvoice.shipToAddress?.address2 ? `\n${selectedInvoice.shipToAddress.address2}` : ''}
                        {selectedInvoice.shipToAddress?.city ? `\n${selectedInvoice.shipToAddress.city}` : ''}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-20">{(selectedInvoice?.quantityDisplay || 'qty') === 'hours' ? 'Hours' : ((selectedInvoice?.quantityDisplay || 'qty') === 'qty_hours' ? 'Qty/Hours' : 'Qty')}</TableHead>
                          <TableHead className="w-24">Rate</TableHead>
                          <TableHead className="w-20">Tax</TableHead>
                          <TableHead className="w-28">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.items?.map((it, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-gray-900">{it.description || '-'}</TableCell>
                            <TableCell className="text-gray-600 whitespace-pre-line">{it.longDescription || '-'}</TableCell>
                            <TableCell>{it.quantity}</TableCell>
                            <TableCell>{formatCurrency(it.rate, selectedInvoice?.currency)}</TableCell>
                            <TableCell>{it.tax || 'No Tax'}</TableCell>
                            <TableCell>{formatCurrency(it.amount, selectedInvoice?.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totals */}
                  <div className="mt-4 flex justify-end">
                    {(() => {
                      const t = computeTotals(selectedInvoice); return (
                        <div className="w-full md:w-1/3 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="font-medium">{formatCurrency(t.subtotal, selectedInvoice?.currency)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Tax</span>
                            <span className="font-medium">{formatCurrency(t.totalTax, selectedInvoice?.currency)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-gray-900 font-semibold">Total</span>
                            <span className="text-gray-900 font-semibold">{formatCurrency(t.total, selectedInvoice?.currency)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog - Matching Create Modal Design */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[1400px] h-[95vh] flex flex-col p-0 rounded-xl overflow-hidden bg-white custom-date-styles">
          <DialogHeader className="px-8 pb-0 pt-8">
            <DialogTitle className="text-2xl font-bold text-gray-900">Edit Invoice</DialogTitle>
            <DialogDescription className="sr-only">
              Edit invoice details, items, and billing information.
            </DialogDescription>
          </DialogHeader>

          {selectedInvoice && (
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-6 space-y-8 bg-white">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className='space-y-2'>
                        <Label htmlFor="customer" className="text-sm font-medium text-gray-700">
                          * Customer
                        </Label>
                        <Select
                          value={selectedInvoice.customerId}
                          onValueChange={(value) => {
                            const client = clients.find(c => c._id === value || c.id === value);
                            setSelectedInvoice(prev => prev ? {
                              ...prev,
                              customerId: value,
                              customer: client ? {
                                id: client._id || client.id,
                                name: client.name,
                                email: client.email,
                                phone: client.phone
                              } : prev.customer
                            } : null);
                          }}
                        >
                          <SelectTrigger className="w-full h-10 border-gray-300">
                            <SelectValue placeholder="Select customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {loadingClients ? (
                              <div className="p-2 text-sm text-gray-500 text-center">
                                Loading clients...
                              </div>
                            ) : clients.length === 0 ? (
                              <div className="p-2 text-sm text-gray-500 text-center">
                                No clients available
                              </div>
                            ) : (
                              clients.map((client) => (
                                <SelectItem key={client._id || client.id} value={client._id || client.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span className="font-medium">{client.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>


                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Edit className="h-4 w-4 text-gray-500" />
                            <Label className="text-sm font-medium text-gray-700">Bill To</Label>
                          </div>
                          <textarea
                            value={selectedInvoice.billToAddress?.address1 || (selectedInvoice.billToAddress as any)?.address || ''}
                            onChange={(e) => {
                              setSelectedInvoice(prev => prev ? {
                                ...prev,
                                billToAddress: {
                                  address1: e.target.value,
                                  address2: prev.billToAddress?.address2 || '',
                                  city: prev.billToAddress?.city || '',
                                  state: prev.billToAddress?.state || '',
                                  country: prev.billToAddress?.country || '',
                                  postalCode: prev.billToAddress?.postalCode || ''
                                }
                              } : null);
                            }}
                            className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md resize-y"
                            placeholder="Enter billing address"
                          />
                        </div>

                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700">Ship to</Label>
                          <textarea
                            value={selectedInvoice.shipToAddress?.address1 || (selectedInvoice.shipToAddress as any)?.address || ''}
                            onChange={(e) => {
                              setSelectedInvoice(prev => prev ? {
                                ...prev,
                                shipToAddress: {
                                  address1: e.target.value,
                                  address2: prev.shipToAddress?.address2 || '',
                                  city: prev.shipToAddress?.city || '',
                                  state: prev.shipToAddress?.state || '',
                                  country: prev.shipToAddress?.country || '',
                                  postalCode: prev.shipToAddress?.postalCode || ''
                                }
                              } : null);
                            }}
                            className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md resize-y"
                            placeholder="Enter shipping address"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="invoiceNumber" className="text-sm font-medium text-gray-700">
                          * Invoice Number
                        </Label>
                        <HelpCircle className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="flex gap-2">
                        <Input value="INV-" disabled className="w-16 h-10 bg-gray-100 border-gray-300" />
                        <Input
                          value={selectedInvoice.invoiceNumber.replace('INV-', '')}
                          disabled
                          className="h-10 border-gray-300 bg-gray-100"
                        />
                        <Input
                          value="10 / 2025"
                          onChange={() => {
                            // This is just a display field, but making it editable for completeness
                          }}
                          className="w-[6rem] h-10 border-gray-300 text-center"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="invoiceDate" className="text-sm font-medium text-gray-700">
                          * Invoice Date
                        </Label>
                        <div className="flex items-center h-10 border border-gray-300 rounded-md overflow-hidden bg-white">
                          <Input
                            id="invoiceDate"
                            type="date"
                            ref={invoiceDateRef}
                            value={selectedInvoice.invoiceDate}
                            onChange={(e) => {
                              setSelectedInvoice(prev => prev ? { ...prev, invoiceDate: e.target.value } : null);
                            }}
                            className="custom-date-input flex-1 h-full border-0 focus-visible:ring-0 focus:outline-none px-3 bg-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => invoiceDateRef.current?.showPicker?.()}
                            className="w-10 h-full flex items-center justify-center border-l border-gray-300 bg-gray-50 cursor-pointer"
                            aria-label="Open invoice date picker"
                          >
                            <Calendar className="h-4 w-4 text-gray-500" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dueDate" className="text-sm font-medium text-gray-700">Due Date</Label>
                        <div className="flex items-center h-10 border border-gray-300 rounded-md overflow-hidden bg-white">
                          <Input
                            id="dueDate"
                            type="date"
                            ref={dueDateRef}
                            value={selectedInvoice.dueDate}
                            onChange={(e) => {
                              setSelectedInvoice(prev => prev ? { ...prev, dueDate: e.target.value } : null);
                            }}
                            className="custom-date-input flex-1 h-full border-0 focus-visible:ring-0 focus:outline-none px-3 bg-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => dueDateRef.current?.showPicker?.()}
                            className="w-10 h-full flex items-center justify-center border-l border-gray-300 bg-gray-50 cursor-pointer"
                            aria-label="Open due date picker"
                          >
                            <Calendar className="h-4 w-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 pt-2">
                      <input
                        type="checkbox"
                        id="preventOverdueReminders"
                        checked={selectedInvoice.preventOverdueReminders}
                        onChange={(e) => {
                          setSelectedInvoice(prev => prev ? { ...prev, preventOverdueReminders: e.target.checked } : null);
                        }}
                        className="border-gray-300"
                      />
                      <Label htmlFor="preventOverdueReminders" className="text-sm text-gray-700">Prevent sending overdue reminders for this invoice</Label>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Allowed payment modes for this invoice</Label>
                      <Select
                        value={selectedInvoice.paymentModes?.[0] || ''}
                        onValueChange={(value) => {
                          setSelectedInvoice(prev => prev ? {
                            ...prev,
                            paymentModes: value ? [value] : []
                          } : null);
                        }}
                      >
                        <SelectTrigger className="w-full h-10 border-gray-300">
                          <SelectValue placeholder="Select payment mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Credit Card">Credit Card</SelectItem>
                          <SelectItem value="Debit Card">Debit Card</SelectItem>
                          <SelectItem value="UPI (Google Pay, PhonePe, Paytm, etc.)">UPI (Google Pay, PhonePe, Paytm, etc.)</SelectItem>
                          <SelectItem value="Bank Transfer (NEFT/RTGS/IMPS)">Bank Transfer (NEFT/RTGS/IMPS)</SelectItem>
                          <SelectItem value="Cheque">Cheque</SelectItem>
                          <SelectItem value="Online Payment Gateway">Online Payment Gateway</SelectItem>
                          <SelectItem value="Wallet Payment">Wallet Payment</SelectItem>
                          <SelectItem value="Apple Pay">Apple Pay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        * Currency
                      </Label>
                      <Select
                        value={selectedInvoice.currency}
                        onValueChange={(value) => {
                          setSelectedInvoice(prev => prev ? {
                            ...prev,
                            currency: value
                          } : null);
                        }}
                      >
                        <SelectTrigger className="w-full h-10 border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD $</SelectItem>
                          <SelectItem value="EUR">EUR €</SelectItem>
                          <SelectItem value="GBP">GBP £</SelectItem>
                          <SelectItem value="INR">INR ₹</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">* Status</Label>
                      <Select
                        value={selectedInvoice.status || ''}
                        onValueChange={(value) => {
                          setSelectedInvoice(prev => prev ? {
                            ...prev,
                            status: value as 'paid' | 'pending' | 'overdue'
                          } : null);
                        }}
                      >
                        <SelectTrigger className="w-full h-10 border-gray-300">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2 h-10 border-gray-300">
                          Add Item
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => {
                          const newItems = [...(selectedInvoice?.items || [])];
                          const unitLabel = quantityDisplayEdit === 'hours' ? 'Hours' : (quantityDisplayEdit === 'qty_hours' ? 'Qty/Hours' : 'Unit');
                          newItems.push({
                            description: '',
                            longDescription: '',
                            quantity: 1,
                            unit: unitLabel,
                            rate: 0,
                            tax: 'No Tax',
                            amount: 0,
                          });
                          setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                        }}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add New Item
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2 h-10 border-gray-300">
                          Bill Tasks
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>
                          <HelpCircle className="h-4 w-4 mr-2" />
                          Bill Time Tracking
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-center gap-6">
                    <Label className="text-sm font-medium text-gray-700">Show quantity as:</Label>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center space-x-2">
                        <input type="radio" id="qty" name="quantityDisplay" value="qty" checked={quantityDisplayEdit === 'qty'} onChange={() => { setQuantityDisplayEdit('qty'); try { if (selectedInvoice) localStorage.setItem(`invoiceQuantityDisplay:${selectedInvoice.id}`, 'qty'); } catch { }; setSelectedInvoice(prev => prev ? { ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Unit' })) } : prev); }} className="text-blue-600" />
                        <Label htmlFor="qty" className="text-sm text-gray-700">Qty</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input type="radio" id="hours" name="quantityDisplay" value="hours" checked={quantityDisplayEdit === 'hours'} onChange={() => { setQuantityDisplayEdit('hours'); try { if (selectedInvoice) localStorage.setItem(`invoiceQuantityDisplay:${selectedInvoice.id}`, 'hours'); } catch { }; setSelectedInvoice(prev => prev ? { ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Hours' })) } : prev); }} className="text-blue-600" />
                        <Label htmlFor="hours" className="text-sm text-gray-700">Hours</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input type="radio" id="qty_hours" name="quantityDisplay" value="qty_hours" checked={quantityDisplayEdit === 'qty_hours'} onChange={() => { setQuantityDisplayEdit('qty_hours'); try { if (selectedInvoice) localStorage.setItem(`invoiceQuantityDisplay:${selectedInvoice.id}`, 'qty_hours'); } catch { }; setSelectedInvoice(prev => prev ? { ...prev, items: (prev.items || []).map(it => ({ ...it, unit: 'Qty/Hours' })) } : prev); }} className="text-blue-600" />
                        <Label htmlFor="qty_hours" className="text-sm text-gray-700">Qty/Hours</Label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="flex items-center gap-1 text-gray-700 font-medium">
                          Item
                          <AlertCircle className="h-3 w-3 text-gray-500" />
                        </TableHead>
                        <TableHead className="text-gray-700 font-medium">Description</TableHead>
                        <TableHead className="text-gray-700 font-medium">{quantityDisplayEdit === 'hours' ? 'Hours' : (quantityDisplayEdit === 'qty_hours' ? 'Qty/Hours' : 'Qty')}</TableHead>
                        <TableHead className="text-gray-700 font-medium">Rate</TableHead>
                        <TableHead className="text-gray-700 font-medium">Tax</TableHead>
                        <TableHead className="flex items-center gap-1 text-gray-700 font-medium">
                          Amount
                          <Settings className="h-3 w-3 text-gray-500" />
                        </TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.items?.map((item, index) => (
                        <TableRow key={index} className="border-b border-gray-200">
                          <TableCell className="p-3">
                            <Textarea
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => {
                                const newItems = [...(selectedInvoice.items || [])];
                                newItems[index] = { ...newItems[index], description: e.target.value };
                                setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                              }}
                              className="min-h-[60px] resize-none border-gray-300"
                            />
                          </TableCell>
                          <TableCell className="p-3">
                            <Textarea
                              placeholder="Long description"
                              value={item.longDescription || ''}
                              onChange={(e) => {
                                const newItems = [...(selectedInvoice.items || [])];
                                newItems[index] = { ...newItems[index], longDescription: e.target.value };
                                setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                              }}
                              className="min-h-[60px] resize-none border-gray-300"
                            />
                          </TableCell>
                          <TableCell className="p-3">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newItems = [...(selectedInvoice.items || [])];
                                    const newQuantity = Number(e.target.value);
                                    newItems[index] = {
                                      ...newItems[index],
                                      quantity: newQuantity,
                                      amount: calculateItemAmount(newQuantity, newItems[index].rate, newItems[index].tax)
                                    };
                                    setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                                  }}
                                  className="w-16 h-8 border-gray-300"
                                />
                                <div className="flex flex-col">
                                  <ChevronUp className="h-3 w-3 text-gray-500 cursor-pointer" />
                                  <ChevronDown className="h-3 w-3 text-gray-500 cursor-pointer" />
                                </div>
                              </div>
                              <Input
                                value={item.unit || 'Unit'}
                                onChange={(e) => {
                                  const newItems = [...(selectedInvoice.items || [])];
                                  newItems[index] = { ...newItems[index], unit: e.target.value };
                                  setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                                }}
                                placeholder="Unit"
                                className="w-16 h-6 text-xs border-gray-300"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="p-3">
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                placeholder="Rate"
                                value={item.rate}
                                onChange={(e) => {
                                  const newItems = [...(selectedInvoice.items || [])];
                                  const newRate = Number(e.target.value);
                                  newItems[index] = {
                                    ...newItems[index],
                                    rate: newRate,
                                    amount: calculateItemAmount(newItems[index].quantity, newRate, newItems[index].tax)
                                  };
                                  setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                                }}
                                className="h-8 border-gray-300"
                              />
                              <div className="flex flex-col">
                                <ChevronUp className="h-3 w-3 text-gray-500 cursor-pointer" />
                                <ChevronDown className="h-3 w-3 text-gray-500 cursor-pointer" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="p-3">
                            <Select
                              value={item.tax || 'No Tax'}
                              onValueChange={(value) => {
                                const newItems = [...(selectedInvoice.items || [])];
                                newItems[index] = {
                                  ...newItems[index],
                                  tax: value,
                                  amount: calculateItemAmount(newItems[index].quantity, newItems[index].rate, value)
                                };
                                setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                              }}
                            >
                              <SelectTrigger className="h-8 border-gray-300 w-[120px]">
                                <SelectValue placeholder="No Tax" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="No Tax">No Tax</SelectItem>
                                <SelectItem value="9%">9%</SelectItem>
                                <SelectItem value="12%">12%</SelectItem>
                                <SelectItem value="18%">18%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-medium">
                                {getCurrencySymbol(selectedInvoice?.currency)}
                                {item.amount.toFixed(2)}
                              </span>
                              <ChevronDown className="h-3 w-3 text-gray-500 cursor-pointer" />
                            </div>
                          </TableCell>
                          <TableCell className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (selectedInvoice.items && selectedInvoice.items.length > 1) {
                                  const newItems = selectedInvoice.items.filter((_, i) => i !== index);
                                  setSelectedInvoice(prev => prev ? { ...prev, items: newItems } : null);
                                }
                              }}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 px-8 py-6 border-t bg-white">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              className="rounded-md"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!selectedInvoice) return;
                try {
                  setSubmitting(true);
                  await invoiceService.updateInvoice(selectedInvoice.id, {
                    customerId: selectedInvoice.customerId,
                    invoiceNumber: selectedInvoice.invoiceNumber,
                    invoiceDate: selectedInvoice.invoiceDate,
                    dueDate: selectedInvoice.dueDate,
                    preventOverdueReminders: selectedInvoice.preventOverdueReminders,
                    paymentModes: selectedInvoice.paymentModes,
                    currency: selectedInvoice.currency,
                    status: selectedInvoice.status,
                    items: selectedInvoice.items,
                    billToAddress: selectedInvoice.billToAddress?.address1 ? {
                      address1: selectedInvoice.billToAddress.address1,
                      address2: selectedInvoice.billToAddress.address2 || '',
                      city: selectedInvoice.billToAddress.city || '',
                      state: selectedInvoice.billToAddress.state || '',
                      country: selectedInvoice.billToAddress.country || '',
                      postalCode: selectedInvoice.billToAddress.postalCode || ''
                    } : undefined,
                    shipToAddress: selectedInvoice.shipToAddress?.address1 ? {
                      address1: selectedInvoice.shipToAddress.address1,
                      address2: selectedInvoice.shipToAddress.address2 || '',
                      city: selectedInvoice.shipToAddress.city || '',
                      state: selectedInvoice.shipToAddress.state || '',
                      country: selectedInvoice.shipToAddress.country || '',
                      postalCode: selectedInvoice.shipToAddress.postalCode || ''
                    } : undefined,
                  });
                  try { localStorage.setItem(`invoiceQuantityDisplay:${selectedInvoice.id}`, quantityDisplayEdit); } catch { }
                  setEditOpen(false);
                  await loadInvoices();
                  toast.success('Invoice has been updated successfully.');
                } catch (error: any) {
                  toast.error('Failed to update invoice');
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              className="rounded-md bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Updating...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              {selectedInvoice ? ` ${selectedInvoice.invoiceNumber}` : ' this invoice'} from your list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 